/**
 * Minimale reserveringstermijn: online boeken kan alleen als het ophalen
 * minstens 3 uur in de toekomst ligt. Is het korter dan dat, dan belt de klant
 * ons en zetten wij de rit handmatig in (zie admin → Handmatige rit).
 *
 * Alle vergelijkingen gebeuren in de Nederlandse tijdzone, ongeacht de
 * tijdzone van het apparaat van de klant.
 */

export const MIN_LEAD_HOURS = 3;
export const BOOKING_PHONE = "085 060 7086";
export const BOOKING_PHONE_TEL = "+31850607086";

const TZ = "Europe/Amsterdam";

/** Huidige Nederlandse datum/tijd als minuten sinds 1970 (pseudo-lokaal). */
function amsterdamParts(now: Date) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(now).map((p) => [p.type, p.value]));
  return {
    year: Number(parts["year"]),
    month: Number(parts["month"]),
    day: Number(parts["day"]),
    hour: Number(parts["hour"] === "24" ? "0" : parts["hour"]),
    minute: Number(parts["minute"]),
  };
}

function toStamp(p: { year: number; month: number; day: number; hour: number; minute: number }) {
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
}

function fromStamp(stamp: number) {
  const d = new Date(stamp);
  const date = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
  const time = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  return { date, time };
}

/** Vroegst mogelijke ophaalmoment (nu + 3 uur), afgerond op 5 minuten omhoog. */
export function earliestPickup(now: Date = new Date()): { date: string; time: string } {
  const stamp = toStamp(amsterdamParts(now)) + MIN_LEAD_HOURS * 3_600_000;
  const rounded = Math.ceil(stamp / 300_000) * 300_000;
  return fromStamp(rounded);
}

/** Vroegste datum die de klant in een datumveld mag kiezen. */
export function earliestPickupDate(now: Date = new Date()): string {
  return earliestPickup(now).date;
}

/** Ligt dit ophaalmoment minstens 3 uur in de toekomst? */
export function leadTimeOk(
  date: string | null | undefined,
  time: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!date || !time) return true;
  const [y, m, d] = date.split("-").map(Number);
  const [h, min] = time.split(":").map(Number);
  if (!y || !m || !d || Number.isNaN(h) || Number.isNaN(min)) return true;
  const pickup = Date.UTC(y, m - 1, d, h ?? 0, min ?? 0);
  const earliest = toStamp(amsterdamParts(now)) + MIN_LEAD_HOURS * 3_600_000;
  return pickup >= earliest;
}

const NL_DATE = new Intl.DateTimeFormat("nl-NL", { day: "numeric", month: "long", timeZone: "UTC" });
const EN_DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", timeZone: "UTC" });

function label(locale: "nl" | "en", iso: string, time: string) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, d ?? 1));
  const day = (locale === "en" ? EN_DATE : NL_DATE).format(dt);
  return locale === "en" ? `${day} at ${time}` : `${day} om ${time}`;
}

/** Uitlegtekst met het vroegst mogelijke moment en ons telefoonnummer. */
export function leadTimeMessage(locale: "nl" | "en" = "nl", now: Date = new Date()): string {
  const first = earliestPickup(now);
  const when = label(locale, first.date, first.time);
  return locale === "en"
    ? `Online bookings need at least ${MIN_LEAD_HOURS} hours' notice. The earliest pick-up you can book online is ${when}. Do you need a taxi sooner? Please call us on ${BOOKING_PHONE} and we will arrange it for you.`
    : `Boek online minimaal ${MIN_LEAD_HOURS} uur voor het ophalen. Het vroegste moment dat u nu online kunt boeken is ${when}. Heeft u eerder een taxi nodig? Bel ons dan even op ${BOOKING_PHONE}, dan regelen wij het voor u.`;
}
